import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import Paper from '@base/Paper/Paper';
import P1 from '@base/P1/P1';
import P2 from '@base/P2/P2';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import NextImage from '@base/FutureImage';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import Me from '@models/Me';
import { getMe } from '@api/selectors/auth';

const GoalCardContainer = styled(Paper)`
  grid-column: ${({ $isMyGoal }) => ($isMyGoal ? 'span 9' : 'span 6')};
  position: relative;
  padding: ${Spacings.SPACING_6B};
  background: var(--color-base-1);
  margin-top: ${Spacings.SPACING_4B};
  border-radius: 8px;
  display: ${({ $isMyGoal }) => ($isMyGoal ? 'flex' : 'block')};
  align-items: center;
  cursor: pointer;
  :hover {
    box-shadow: ${boxShadowGenerator({
      yOffset: Spacings.SPACING_1B,
      blur: Spacings.SPACING_2B
    })};
  }
  transition: box-shadow 0.2s ease-in-out;
  ${mediaQuery.uptoTablet} {
    grid-column: span 9;
    display: block;
  }
  ${mediaQuery.uptoMobile} {
    display: flex;
    align-items: center;
  }
`;

const GoalName = styled.span`
  color: ${({ $isAvailable }) =>
    $isAvailable ? 'var(--color-text-primary)' : 'var(--color-text-secondary)'};
  font-weight: ${Fonts.SEMIBOLD};
  ${mediaQuery.uptoMobile} {
    max-width: 170px;
  }
`;

const GoalIcon = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-right: ${({ $isMyGoal }) => ($isMyGoal ? Spacings.SPACING_5B : 0)};
  margin-bottom: ${({ $isMyGoal }) => ($isMyGoal ? 0 : Spacings.SPACING_5B)};
  ${mediaQuery.uptoMobile} {
    margin-bottom: 0;
    margin-right: ${Spacings.SPACING_4B};
  }
`;

const CoursesLength = styled(P2)`
  margin-top: ${Spacings.SPACING_1B};
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_2};
  }
`;

const ExpandIcon = styled.div`
  position: absolute;
  right: ${Spacings.SPACING_2B};
  top: 25%;
`;

const Highlight = styled.span`
  background-color: var(--color-i-current-highlight);
`;

const Tags = styled.div`
  padding: ${Spacings.SPACING_2} ${Spacings.SPACING_1B};
  border-radius: ${Spacings.SPACING_1B};
  background-color: var(--color-divider);
  color: var(--color-text-secondary);
  font-size: ${Fonts.EXTRA_SMALL};
  font-weight: ${Fonts.BOLD};
  ${mediaQuery.uptoMobile} {
    display: none;
  }
`;

const MobileTags = styled(Tags)`
  display: none;
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_1B};
    display: block;
  }
`;

const RenderGoalName = ({ goal, highlightValue }) => {
  const start = goal.name
    .toLowerCase()
    .indexOf(highlightValue.trim().toLowerCase());
  const end = start + highlightValue.length;
  const isAvailable = goal.isForeignGoal ? goal.isAvailable : true;

  if (!highlightValue || start === -1) {
    return <GoalName $isAvailable={isAvailable}>{goal.name}</GoalName>;
  }

  return (
    <>
      <GoalName $isAvailable={isAvailable}>
        {goal.name.substr(0, start)}
      </GoalName>
      <GoalName $isAvailable={isAvailable}>
        <Highlight>{goal.name.substr(start, end - start)}</Highlight>
      </GoalName>
      <GoalName $isAvailable={isAvailable}>{goal.name.substr(end)}</GoalName>
    </>
  );
};

const MobileGoalCard = ({
  goal,
  isMyGoal,
  highlightValue,
  onGoalCardClick,
  followedGoals
}) => {
  const me = useMyInfo(getMe);
  if (!goal || !goal.name || !goal.uid) return null;
  const subscriptionInfo = Me.getSubscriptionInfo(me, goal.uid) || '';
  const isFollowedGoal = !!followedGoals.find(
    (followedGoal) => followedGoal.uid === goal.uid
  );

  const handleGoalCardClick = () => {
    if (goal.isForeignGoal && !goal.isAvailable) return null;
    const isSubscribedGoal = Me.hasActiveSubscriptionToGoal(me, goal.uid);
    onGoalCardClick({
      isMyGoal,
      isSubscribedGoal,
      isFollowedGoal,
      goal,
      evtSource: 'Default List'
    });
  };

  return (
    <GoalCardContainer
      className="goal-card-container"
      key={`goal-${goal.uid}`}
      $isMyGoal={isMyGoal}
      onClick={handleGoalCardClick}
    >
      {goal.icon && (
        <GoalIcon $isMyGoal={isMyGoal}>
          <NextImage src={goal.icon} alt={goal.name} height={40} width={40} />
          {goal.isForeignGoal && !goal.isAvailable && <Tags>SOON</Tags>}
        </GoalIcon>
      )}
      <div>
        <P1>
          <RenderGoalName goal={goal} highlightValue={highlightValue} />
        </P1>
        {!isMyGoal ? (
          <CoursesLength color="var(--color-text-secondary)">
            {subscriptionInfo || (isFollowedGoal && 'Free plan')}
          </CoursesLength>
        ) : null}
      </div>
      <ExpandIcon>
        {goal.isForeignGoal && !goal.isAvailable ? (
          <MobileTags>SOON</MobileTags>
        ) : (
          <NextImage src="expand.svg" alt={goal.name} height={24} width={24} />
        )}
      </ExpandIcon>
    </GoalCardContainer>
  );
};

export default MobileGoalCard;

MobileGoalCard.propTypes = {
  goal: PropTypes.shape({
    icon: PropTypes.string,
    name: PropTypes.string,
    uid: PropTypes.string
  }).isRequired,
  isMyGoal: PropTypes.bool.isRequired,
  onGoalCardClick: PropTypes.func.isRequired,
  highlightValue: PropTypes.string
};

MobileGoalCard.defaultProps = {
  highlightValue: ''
};
